import { DateSelectorPopover } from '@/components/ui/date-selector/date-selector-popover';
import { PDKDeliveryDateIcon } from '@/components/ui/pdk-icons';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import type { Deal } from '@/models/registry';
import { dealStore } from '@/models/stores';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export type DeliveryDateSelectorProps = {
  delivery_date: Deal['delivery_date'];
  dealId: Deal['id'];
  hideIcon?: boolean;
  hideYear?: boolean;
  dateClassName?: string;
  side?: 'left' | 'top' | 'right' | 'bottom' | undefined;
  align?: 'center' | 'start' | 'end' | undefined;
  withHotkeyListener?: boolean;
  withInput?: boolean;
  className?: string;
  placeholder?: React.ReactNode;
  icon?: React.ReactNode;
};

const DeliveryDateSelector = ({
  delivery_date,
  dealId,
  hideIcon = false,
  hideYear = false,
  dateClassName,
  side = 'left',
  align = 'start',
  withHotkeyListener = false,
  withInput = false,
  className,
  placeholder = 'Delivery Date',
  icon = <PDKDeliveryDateIcon />,
}: DeliveryDateSelectorProps) => {
  const queryClient = useQueryClient();
  const updateDeliveryDateMutation = useMutation({
    onMutate: async ({ delivery_date }) => {
      if (!delivery_date) {
        return;
      }

      dealStore.updateDeliveryDate({
        id: dealId,
        deliveryDate: delivery_date,
      });
    },
    mutationFn: async ({
      delivery_date,
    }: {
      delivery_date: Deal['delivery_date'];
    }) => {
      const response = await pdkAxios
        .post(`/deals/${dealId}/delivery-date`, { delivery_date })
        .catch((e) => {
          console.error('Error updating delivery date', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error updating delivery date', error, variables);
    },
    onSettled: () => {
      // DEV NOTE: invalidateQueries is cause a flicker bug
      // because the useQuery hook in useDeal re runs and gives back an old value
      // It may not even be refetching ...
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] });
    },
  });

  return (
    <DateSelectorPopover
      date={delivery_date}
      hideIcon={hideIcon}
      hideYear={hideYear}
      dateClassName={dateClassName}
      side={side}
      align={align}
      withHotkeyListener={withHotkeyListener}
      hotkey={HOTKEYS.DEAL_DELIVERY_DATE}
      onSelectDate={(date) => {
        if (!date) {
          return;
        }

        updateDeliveryDateMutation.mutate({
          delivery_date: date.toISOString(),
        });
      }}
      withInput={withInput}
      className={className}
      icon={icon}
      placeholder={placeholder}
      inputKey="delivery-date-selector-input"
    />
  );
};

export default DeliveryDateSelector;
